import { FC, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { QuoteExperienceState } from '../QuoteExperienceQuickSetup.page';
import { ApiServiceContext } from '../../../api/ApiService.context';
import { BundleAttribution } from '../../../api/bundle/types';
import { Attribution } from '../../../api/attribution/types';
import { BundleService } from '../../../api/bundle/BundleService.class';
import { AttributionService } from '../../../api/attribution/AttributionService.class';
import { QuoteExperienceService } from '../../../api/quote-experience/QuoteExperienceService.class';
import { Box } from '@mui/material';
import { getLatestQuoteSchemas, QuoteSchema } from '../../../api/quote-experience/QuoteExperienceProcessor';

import { buildSchemaValidationState, formatSchemaTypeTitle } from '../QuoteExperience.helpers';
import { QEFormEntryMode, QuoteExperiencePanel } from '../components/QuoteExperiencePanel/QuoteExperiencePanel.component';
import { QuoteExperienceSchemaPanelWrapper } from '../panels/QuoteExperienceSchemaPanel/QuoteExperienceSchemaPanelWrapper';
import { ImageEnrichedSchemaPanelWrapper } from '../panels/QuoteExperienceSchemaPanel/ImageEnrichedSchemaPanelWrapper';
import { ImageManagerPanel } from '../panels/ImageManager/ImageManager.panel';
import { FileManagerPanel } from '../panels/FileManager/FileManager.panel';
import { QuoteExperienceQuickSetupForm } from '../customForms/QuoteExperienceQuickSetup.form';
import { QuoteExperienceConfigForm } from '../customForms/QuoteExperienceConfig.form';
import { computeSchemaDefaults } from '../../../utils/jsonSchemaDefaults';

const MANUAL_FORM_OPTIONS: string[] = ['QUICK_SETUP', 'IMAGE_MANAGER', 'FILE_MANAGER'];

type DataLoading = 'NOT STARTED' | 'LOADING' | 'DONE';

export const QuoteExperiencePanels: FC<{
    organizationGuid: string;
    bundleGuid: string | undefined;
    initial: Partial<QuoteExperienceState>;
    current: Partial<QuoteExperienceState>;
    setIsLoading: (key: string, value: boolean) => void;
    updateData: (pointer: string, value: any, setInitial?: boolean) => void;
    setCanSaveBySection: (section: string, canSave: string | null) => void;
}> = ({ bundleGuid, initial, current, setIsLoading, updateData, setCanSaveBySection }) => {
    const { getApiServiceCollections } = useContext(ApiServiceContext);
    const { BundleService, AttributionService, QuoteExperienceService } = getApiServiceCollections() as {
        BundleService: BundleService;
        AttributionService: AttributionService;
        QuoteExperienceService: QuoteExperienceService;
    };
    const [generalError, setGeneralError] = useState<string>('');
    const [selectedForm, setSelectedForm] = useState<string>('QUICK_SETUP');
    const [configFormMode, setConfigFormMode] = useState<QEFormEntryMode>('form');
    const [formOptions, setFormOptions] = useState<string[]>([...MANUAL_FORM_OPTIONS]);
    const [schemas, setSchemas] = useState<Record<string, QuoteSchema>>({});
    const [saveErrorsByType] = useState<Record<string, string>>({});

    const [schemaLoadState, setSchemaLoadState] = useState<DataLoading>('NOT STARTED');
    const [bundleLoadState, setBundleLoadState] = useState<DataLoading>('NOT STARTED');

    const canLoadPanels = useMemo(() =>
        schemaLoadState === 'DONE' && (!bundleGuid || bundleLoadState === 'DONE'),
        [schemaLoadState, bundleGuid, bundleLoadState]);

    const { canSavePanels, validationMessages } = useMemo(() => {
        const { canSave, requiredDetails } = buildSchemaValidationState(
            Object.values(schemas),
            current.formDataByType || {}
        );
        return { canSavePanels: canSave, validationMessages: requiredDetails };
    }, [schemas, current]);

    useEffect(() => {
        setCanSaveBySection('panels', canSavePanels ? null : 'There are one or more errors related to the form data provided. Please correct these errors before saving.');
    }, [canSavePanels, setCanSaveBySection]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const getSchemasByType = AttributionService.getAttributionSchemasByType;
                setIsLoading('Schemas', true);
                setGeneralError('');
                let resp = await getSchemasByType({ type: 'quote_experience' });
                while (resp.retry) {
                    resp = await resp.retry.catch(error => error);
                }
                // TODO - Properly error out when resp.error here
                const raw = resp?.jsonBody?.schemas ?? [];
                const list = getLatestQuoteSchemas(raw);
                const formDataByType = list.reduce((acc, cur) => {
                    try {
                        acc[cur.schema_type] = computeSchemaDefaults(cur.schema);
                    } catch (err) {
                        console.log(`Failed to set form defaults for schema "${cur.schema_type}". Error: ${err}`);
                        acc[cur.schema_type] = {};
                    }
                    return acc;
                }, {} as Record<string, Record<string, any>>);
                updateData('/formDataByType', formDataByType);
                const schemaMap = list.reduce((acc, cur) => {
                    acc[cur.schema_type] = cur;
                    return acc;
                }, {} as Record<string, QuoteSchema>);
                setSchemas(schemaMap);
                setFormOptions(prev => {
                    const allKeys = new Set(prev.concat(Object.keys(schemaMap)));
                    return Array.from(allKeys);
                });
                updateData('/schemaVersionByType', list.reduce((acc, cur) => {
                    acc[cur.schema_type] = cur.schema_type_version || 0;
                    return acc;
                }, {} as Record<string, number>), true);
            } catch (err) {
                setGeneralError('Failed to load quote experience schemas');
            } finally {
                setIsLoading('Schemas', false);
                setSchemaLoadState('DONE');
            }
        };
        if (schemaLoadState === 'NOT STARTED') {
            setSchemaLoadState('LOADING');
            fetchData();
        }
        return () => {
            // Any necessary cleanup
        };
    }, [schemaLoadState, AttributionService, updateData, setIsLoading]);


    useEffect(() => {
        const fetchData = async () => {
            setIsLoading('QuoteExperienceForms', true);
            try {
                // Load bundle attributions
                const getBundleAttributions = BundleService.getAttributions;
                let bundleAttrResp = await getBundleAttributions(bundleGuid);
                while (bundleAttrResp.retry) {
                    bundleAttrResp = await bundleAttrResp.retry.catch(error => error);
                }
                // TODO - Properly error out here when bundleAttrResp.error
                const allBundleAttrs: BundleAttribution[] = bundleAttrResp?.jsonBody || [];
                const partnerAttr = allBundleAttrs.find((ba: BundleAttribution) => ba.type === 'PARTNER');
                if (partnerAttr) {
                    updateData('/partnerAttribution', partnerAttr.attributionGuid, true);
                }
                const bundleAttrs = allBundleAttrs.filter((ba: BundleAttribution) => ba.type !== 'PARTNER');
                const attrGuidsByType = bundleAttrs.reduce((acc, cur) => {
                    // Normalize type to match formDataByType keys
                    const normalizedType = QuoteExperienceService.normalizeType(cur.type);
                    acc[normalizedType] = cur.attributionGuid;
                    return acc;
                }, {} as Record<string, string>);
                updateData('/attrGuidByType', attrGuidsByType, true);

                // Load attribution data
                const getAttributionByGuid = AttributionService.getAttributionById;
                const entries = await Promise.all(
                    bundleAttrs.map(async (attr) => {
                        let aResp = await getAttributionByGuid(attr.attributionGuid);
                        while (aResp.retry) {
                            aResp = await aResp.retry.catch(error => error);
                        }
                        return [attr.attributionGuid, aResp?.jsonBody as Attribution];
                    })
                );

                const byGuid: Record<string, Attribution> = Object.fromEntries(entries);
                const formDataByType: Record<string, any> = {};

                for (const [, a] of Object.entries(byGuid)) {
                    const schemaType = QuoteExperienceService.normalizeType(a?.type);
                    const current = formDataByType[schemaType] ?? {};
                    const merged = { ...current, ...(a?.properties ?? {}) };
                    formDataByType[schemaType] = merged;
                }

                setFormOptions(prev => {
                    const allKeys = new Set(prev.concat(Object.keys(formDataByType)));
                    return Array.from(allKeys);
                });

                updateData('/formDataByType', (existing: any) => {
                    const merged = { ...(existing || {}) };
                    Object.entries(formDataByType).forEach(([key, data]) => {
                        merged[key] = { ...(merged[key] || {}), ...(data as Record<string, any>) };
                    });
                    return merged;
                }, true);
            } catch (err) {
                setGeneralError('Failed to load quote experience schemas');
            } finally {
                setIsLoading('QuoteExperienceForms', false);
                setBundleLoadState('DONE');
            }
        };
        if (bundleGuid && bundleLoadState === 'NOT STARTED' && schemaLoadState === 'DONE') {
            setBundleLoadState('LOADING');
            fetchData();
        }
        return () => {
            // Any necessary cleanup
        };
    }, [bundleGuid, schemaLoadState, bundleLoadState, BundleService, updateData, setIsLoading, AttributionService, QuoteExperienceService]);

    const quickSetupSaveErrors = useMemo(() =>
        Object.values(saveErrorsByType)
        , [saveErrorsByType]);

    const getSelectedForm = useCallback(() => {
        switch (selectedForm) {
            case 'QUICK_SETUP':
                return (
                    <QuoteExperiencePanel
                        saveErrors={quickSetupSaveErrors}
                        formTitle='Quick Setup'
                        validationMessages={validationMessages}
                        showFormSelect={true}
                        selectedForm={selectedForm}
                        selectFormOptions={formOptions}
                        onFormSelected={(option: string) => setSelectedForm(option)}
                        showFormToggle={false}
                    >
                        <QuoteExperienceQuickSetupForm
                            initial={initial}
                            current={current}
                            schemas={schemas}
                            bundleGuid={bundleGuid}
                            setIsLoading={setIsLoading}
                            updateData={updateData}
                        ></QuoteExperienceQuickSetupForm>
                    </QuoteExperiencePanel>
                )
            case 'QUOTE_EXPERIENCE_CONFIG':
                return (
                    <QuoteExperiencePanel
                        saveErrors={quickSetupSaveErrors}
                        formTitle={formatSchemaTypeTitle(selectedForm)}
                        validationMessages={validationMessages}
                        showFormSelect={true}
                        selectedForm={selectedForm}
                        selectFormOptions={formOptions}
                        onFormSelected={(option: string) => setSelectedForm(option)}
                        showFormToggle={true}
                        formMode={configFormMode}
                        onFormModeToggle={setConfigFormMode}
                    >
                        <QuoteExperienceConfigForm
                            initial={initial}
                            current={current}
                            schemas={schemas}
                            bundleGuid={bundleGuid}
                            setIsLoading={setIsLoading}
                            updateData={updateData}
                            formMode={configFormMode}
                            onFormModeToggle={setConfigFormMode}
                        ></QuoteExperienceConfigForm>
                    </QuoteExperiencePanel>
                )
            case 'IMAGE_MANAGER':
                return (
                    <QuoteExperiencePanel
                        formTitle='Image Manager'
                        showFormSelect={true}
                        selectedForm={selectedForm}
                        selectFormOptions={formOptions}
                        onFormSelected={(option: string) => setSelectedForm(option)}
                        showFormToggle={false}
                    >
                        <ImageManagerPanel
                            bundleGuid={bundleGuid}
                            current={current}
                            updateData={updateData}
                            setIsLoading={setIsLoading}
                        />
                    </QuoteExperiencePanel>
                )
            case 'FILE_MANAGER':
                return (
                    <QuoteExperiencePanel
                        formTitle='File Manager'
                        showFormSelect={true}
                        selectedForm={selectedForm}
                        selectFormOptions={formOptions}
                        onFormSelected={(option: string) => setSelectedForm(option)}
                        showFormToggle={false}
                    >
                        <FileManagerPanel
                            bundleGuid={bundleGuid}
                            current={current}
                            updateData={updateData}
                            setIsLoading={setIsLoading}
                        />
                    </QuoteExperiencePanel>
                )
            case 'QUOTE_EXPERIENCE_IMAGES':
                return (
                    <ImageEnrichedSchemaPanelWrapper
                        bundleGuid={bundleGuid}
                        saveErrors={[saveErrorsByType[selectedForm]]}
                        formTitle={formatSchemaTypeTitle(selectedForm)}
                        defaultMode={'form'}
                        validationMessages={validationMessages}
                        showFormToggle={true}
                        showFormSelect={true}
                        selectedForm={selectedForm}
                        selectFormOptions={formOptions}
                        onFormSelected={(option: string) => setSelectedForm(option)}
                        schema={schemas[selectedForm]?.schema}
                        schemaType={selectedForm}
                        current={current}
                        updateData={updateData}
                    />
                )
            case 'QUOTE_EXPERIENCE_ICONS':
                return (
                    <ImageEnrichedSchemaPanelWrapper
                        baseImagePath='icons'
                        bundleGuid={bundleGuid}
                        saveErrors={[saveErrorsByType[selectedForm]]}
                        formTitle={formatSchemaTypeTitle(selectedForm)}
                        defaultMode={'form'}
                        validationMessages={validationMessages}
                        showFormToggle={true}
                        showFormSelect={true}
                        selectedForm={selectedForm}
                        selectFormOptions={formOptions}
                        onFormSelected={(option: string) => setSelectedForm(option)}
                        schema={schemas[selectedForm]?.schema}
                        schemaType={selectedForm}
                        current={current}
                        updateData={updateData}
                    />
                )
            default:
                return (
                    <QuoteExperienceSchemaPanelWrapper
                        saveErrors={[saveErrorsByType[selectedForm]]}
                        formTitle={formatSchemaTypeTitle(selectedForm)}
                        defaultMode={'form'}
                        validationMessages={validationMessages}
                        showFormToggle={true}
                        showFormSelect={true}
                        selectedForm={selectedForm}
                        selectFormOptions={formOptions}
                        onFormSelected={(option: string) => setSelectedForm(option)}
                        schema={schemas[selectedForm]?.schema}
                        schemaType={selectedForm}
                        current={current}
                        updateData={updateData}
                    />
                )
        }
    }, [selectedForm, schemas, initial, current, updateData, setIsLoading,
        quickSetupSaveErrors, saveErrorsByType, validationMessages, formOptions, bundleGuid, configFormMode
    ]);

    return (
        <Box
            sx={{
                marginTop: 2,
                padding: 2,
                border: '1px solid',
                borderColor: generalError ? 'error.main' : 'divider',
                borderRadius: 1,
                backgroundColor: 'background.paper',
            }}
        >
            {
                canLoadPanels ? getSelectedForm() : null
            }
        </Box>
    );
}